import { useState } from "react";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import type { AlertRule, AlertRuleConfig, AlertType } from "@/services/alert-rules.service";

// Limites que o próprio cliente ajusta ao lado do toggle de notificação
// (pedido 2026-09-20). Só os tipos que precisam de um valor pra disparar.
export const LIMIT_EDITABLE_TYPES: AlertType[] = ["overspeed", "no_signal", "off_hours", "displacement"];

export function isLimitEditable(type: string | undefined): boolean {
  return !!type && (LIMIT_EDITABLE_TYPES as string[]).includes(type);
}

const DEFAULT_OFF_HOURS = {
  weekdaysStart: "06:00",
  weekdaysEnd: "22:00",
  weekendEnabled: true,
  weekendStart: "06:00",
  weekendEnd: "22:00",
};

// Texto curto do limite atual, mostrado sob o nome do alerta.
export function limitSummary(type: string | undefined, config: AlertRuleConfig | null | undefined): string | null {
  if (!config) return null;
  switch (type) {
    case "overspeed":
      return config.maxSpeed ? `acima de ${config.maxSpeed} km/h` : null;
    case "no_signal":
      return config.noSignalMinutes
        ? config.noSignalMinutes >= 60 && config.noSignalMinutes % 60 === 0
          ? `sem sinal há ${config.noSignalMinutes / 60}h`
          : `sem sinal há ${config.noSignalMinutes} min`
        : null;
    case "displacement":
      return config.maxDistanceKm ? `acima de ${config.maxDistanceKm} km/dia` : null;
    case "off_hours":
      return config.offHours ? `permitido ${config.offHours.weekdaysStart}–${config.offHours.weekdaysEnd}` : null;
    default:
      return null;
  }
}

interface Props {
  type: AlertType;
  rule: AlertRule | undefined;
  saving: boolean;
  onSave: (config: AlertRuleConfig, actionKind?: "notify" | "block") => void;
}

// Botão de engrenagem + painel inline com os campos do tipo. Usado no
// app mobile e na aba Alertas do painel desktop.
export function AlertLimitsEditor({ type, rule, saving, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const cfg = rule?.config ?? null;
  const [maxSpeed, setMaxSpeed] = useState(String(cfg?.maxSpeed ?? 80));
  const [noSignalMin, setNoSignalMin] = useState(String(cfg?.noSignalMinutes ?? 30));
  const [maxKm, setMaxKm] = useState(String(cfg?.maxDistanceKm ?? 100));
  const [oh, setOh] = useState(cfg?.offHours ?? DEFAULT_OFF_HOURS);
  const [blockWhenMoving, setBlockWhenMoving] = useState(rule?.actionKind === "block");

  const submit = () => {
    if (type === "overspeed") {
      const n = Number(maxSpeed);
      if (!(n > 0)) return;
      onSave({ maxSpeed: n });
    } else if (type === "no_signal") {
      const n = Math.round(Number(noSignalMin));
      if (!(n > 0)) return;
      onSave({ noSignalMinutes: n });
    } else if (type === "displacement") {
      const n = Number(maxKm);
      if (!(n > 0)) return;
      onSave({ maxDistanceKm: n });
    } else if (type === "off_hours") {
      onSave({ offHours: oh }, blockWhenMoving ? "block" : "notify");
    }
    setOpen(false);
  };

  const timeInput = (value: string, onChange: (v: string) => void) => (
    <Input type="time" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-28" />
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
        aria-label="Ajustar limites"
        aria-expanded={open}
      >
        <Settings2 className="h-4 w-4" />
      </button>
      {open && (
        <div className="basis-full space-y-3 border-t border-border pt-3 text-sm">
          {type === "overspeed" && (
            <label className="flex items-center justify-between gap-3">
              Limite de velocidade (km/h)
              <Input type="number" min={1} value={maxSpeed} onChange={(e) => setMaxSpeed(e.target.value)} className="h-9 w-28" />
            </label>
          )}
          {type === "no_signal" && (
            <label className="flex items-center justify-between gap-3">
              Avisar após (minutos sem comunicar)
              <Input type="number" min={1} value={noSignalMin} onChange={(e) => setNoSignalMin(e.target.value)} className="h-9 w-28" />
            </label>
          )}
          {type === "displacement" && (
            <label className="flex items-center justify-between gap-3">
              Limite de km rodados por dia
              <Input type="number" min={1} value={maxKm} onChange={(e) => setMaxKm(e.target.value)} className="h-9 w-28" />
            </label>
          )}
          {type === "off_hours" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                Seg–Sex permitido
                <span className="flex items-center gap-1">
                  {timeInput(oh.weekdaysStart, (v) => setOh({ ...oh, weekdaysStart: v }))}
                  às
                  {timeInput(oh.weekdaysEnd, (v) => setOh({ ...oh, weekdaysEnd: v }))}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                Circula no fim de semana
                <Switch checked={oh.weekendEnabled} onCheckedChange={(c) => setOh({ ...oh, weekendEnabled: c })} />
              </div>
              {oh.weekendEnabled && (
                <div className="flex items-center justify-between gap-3">
                  Sáb–Dom permitido
                  <span className="flex items-center gap-1">
                    {timeInput(oh.weekendStart, (v) => setOh({ ...oh, weekendStart: v }))}
                    às
                    {timeInput(oh.weekendEnd, (v) => setOh({ ...oh, weekendEnd: v }))}
                  </span>
                </div>
              )}
              <div className="space-y-1.5 pt-1">
                <div className="text-muted-foreground">Ao se mover fora do horário</div>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    [false, "Só alertar"],
                    [true, "Alertar e bloquear"],
                  ] as const).map(([val, label]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setBlockWhenMoving(val)}
                      aria-pressed={blockWhenMoving === val}
                      className={`rounded-lg border px-2 py-2 text-xs font-medium ${
                        blockWhenMoving === val
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {blockWhenMoving && (
                  <p className="text-xs text-muted-foreground">
                    O veículo será bloqueado automaticamente quando estiver em movimento fora do horário.
                  </p>
                )}
              </div>
            </div>
          )}
          <Button type="button" size="sm" className="w-full" disabled={saving} onClick={submit}>
            Salvar limites
          </Button>
        </div>
      )}
    </>
  );
}
